// Toast copy for the Routines write cohort (meta v1.1).
//
// THE FAMILY RECORD SAID THIS COULD NOT BE TIER T1, AND THE TREE SAYS
// OTHERWISE. The `mutation` note carried forward that "six of the seven toasts
// branch or interpolate (T2 on the ladder)", which is why the slice was
// described as a ports inversion for its copy as well as its analytics.
// Re-measured against the source before writing a line of this file: all SEVEN
// `toast(...)` calls under `src/features/workflows/**` are a single
// `intl.formatMessage({ defaultMessage, id })` with NO `values` argument, no
// ternary between two messages, and no component state in the copy — nine
// static descriptors across seven toasts. That is the definition of T1 under
// v1.1, so the copy travels with the mutation as inert data and the app side
// stops calling `toast` for Routines entirely.
//
// THE IDS ARE MOVED, NOT MINTED. `10NAXXGFZy`, `GdA66FDwyH`, `MZ/WY7z1jQ`,
// `uF8xenB4Wz`, `W/5hwrn09m`, `r56rp/hjBq`, `WnAsx4+Fmf` and `dlk5tEaEKp` are
// transcribed verbatim from the pre-carve hooks. An id is what ties a string to
// its Crowdin translations; re-hashing one ships untranslated copy to every
// non-English user while English still looks right — invisible in review and in
// an English-only test. `keys.test`-style equality over this object is what
// pins them.
//
// WHY `defineMessages` AND NOT A PLAIN OBJECT LITERAL. The formatjs extractor
// recognises the CALL, not the shape. A literal with an `id` type-checks,
// renders correctly in English, and is then dropped from the catalog on the next
// extract — deleting eight live translations with nothing failing. The
// `.messages.ts` basename is what the localisation workflow's path filter
// watches.
//
// WHY THIS IS LEGAL INSIDE `/data`. `defineMessages` is an identity function
// over data: nothing renders, no `IntlShape` is read, and this package still
// imports no design system. The rendering half is the app's single
// `MutationCache` subscriber, which formats these through
// `formatMessageOutsideReact`.
//
// ONE ORDERING DELTA SHIPS WITH THE TIER, disclosed rather than discovered:
// TanStack fires the cache-level handler BEFORE the mutation's own `onError`, so
// on the two optimistic hooks the toast now precedes the cache rollback instead
// of following it. Both are synchronous in the same tick and the rollback is
// what the user sees, so there is no product difference — but it is a real
// reordering. Rehydration is the other half of the standard disclosure and is
// not reachable here: none of these mutations declares a `mutationKey`, and the
// app persists mutations only at the TanStack default (`isPaused`), so a
// Routines write cannot be restored and re-toasted at boot.
import type { MutationToastMeta } from '@heidi/api/meta';
import { defineMessages } from 'react-intl';

const messages = defineMessages({
  toggleEnabledErrorTitle: {
    defaultMessage: 'Failed to update routine',
    id: '10NAXXGFZy',
  },
  shareErrorTitle: {
    defaultMessage: 'Failed to share routine',
    id: 'GdA66FDwyH',
  },
  unshareErrorTitle: {
    defaultMessage: 'Failed to update sharing',
    id: 'MZ/WY7z1jQ',
  },
  runErrorTitle: {
    defaultMessage: 'Failed to run routine',
    id: 'uF8xenB4Wz',
  },
  runErrorDescription: {
    defaultMessage: 'Something went wrong. Please try again.',
    id: 'W/5hwrn09m',
  },
  scheduleErrorTitle: {
    defaultMessage: 'Failed to update schedule',
    id: 'r56rp/hjBq',
  },
  eventRulesErrorTitle: {
    defaultMessage: 'Failed to update event triggers',
    id: 'WnAsx4+Fmf',
  },
  eventRulesErrorDescription: {
    defaultMessage: 'Some changes may not have been saved. Please try again.',
    id: 'dlk5tEaEKp',
  },
});

/**
 * One entry per hook, held as named constants rather than inlined at each
 * `useMutation`, so the copy-fidelity suite has a single object to assert
 * against and every user-visible Routines string is readable in one place.
 *
 * `satisfies` rather than `:` keeps the literal types, so a typo in a key fails
 * HERE instead of producing a mutation that declares no toast and silently
 * stops toasting — the exact failure this mechanism makes possible.
 *
 * `appendBackendDetail` is deliberately absent from all seven. None of the
 * pre-carve toasts read `error.response`, so setting it would show users a
 * server string they were never shown before — a widening dressed as a move.
 * `run` and `eventRules` keep their own second sentence instead, which is what
 * the source had.
 */
export const ROUTINE_TOASTS = {
  toggleEnabled: { errorToast: { title: messages.toggleEnabledErrorTitle } },
  share: { errorToast: { title: messages.shareErrorTitle } },
  unshare: { errorToast: { title: messages.unshareErrorTitle } },
  run: {
    errorToast: {
      title: messages.runErrorTitle,
      description: messages.runErrorDescription,
    },
  },
  toggleSchedulePaused: { errorToast: { title: messages.scheduleErrorTitle } },
  setEventRules: {
    errorToast: {
      title: messages.eventRulesErrorTitle,
      description: messages.eventRulesErrorDescription,
    },
  },
} satisfies Record<string, MutationToastMeta>;
